import { useParams } from 'react-router-dom';
import {
  Card,
  Descriptions,
  Space,
  Button,
  Spin,
  Alert,
  Typography,
  Modal,
  Input,
  Select,
  message,
  Tag,
  Row,
  Col,
  Divider,
  Tooltip,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  ReloadOutlined,
  PauseOutlined,
  StopOutlined,
  DollarOutlined,
  ThunderboltOutlined,
  WarningOutlined,
  PlayCircleOutlined,
  CaretRightOutlined,
  SendOutlined,
  DownloadOutlined,
  SoundOutlined,
} from '@ant-design/icons';
import { useState } from 'react';
import {
  useOrder,
  useOrderHistory,
  useApproveOrder,
  useRejectOrder,
  useRegenerateLyrics,
  useRetryGeneration,
  useCancelOrder,
  usePauseOrder,
  useResumeOrder,
  useConfirmPayment,
  useForceTransition,
  useResendSong,
  useSunoBalance,
} from '../hooks/useOrders';
import { StatusBadge } from '../components/StatusBadge';
import { FSMTimeline } from '../components/FSMTimeline';
import { formatDate, formatCurrency, formatPhone } from '../utils/formatters';
import { CHANNEL_LABELS } from '../utils/constants';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

// ─── Song Generation Status Block ────────────────────────────────────────────

const SUNO_STATUS_COLOR: Record<string, string> = {
  pending: 'default',
  processing: 'processing',
  complete: 'success',
  error: 'error',
  failed: 'error',
};

const SUNO_STATUS_LABEL: Record<string, string> = {
  pending: 'Очікує',
  processing: 'Генерується',
  complete: 'Готово',
  error: 'Помилка',
  failed: 'Провалено',
};

interface SongGenBlockProps {
  order: import('../types').Order;
  onRetry: () => void;
  retryLoading: boolean;
}

const SongGenerationBlock: React.FC<SongGenBlockProps> = ({ order, onRetry, retryLoading }) => {
  const { data: sunoBalance, refetch: refetchBalance, isFetching: balanceFetching } = useSunoBalance();

  const isSunoState = ['SUNO_GENERATING', 'LYRICS_APPROVED'].includes(order.status);
  const hasError = !!order.song_error;
  const hasSongStatus = !!order.song_status;
  const hasBalance = !!sunoBalance;

  if (!isSunoState && !hasError && !hasSongStatus && !hasBalance && !balanceFetching) return null;

  const totalCredits = sunoBalance?.total_credits ?? null;
  const lowCredits = totalCredits !== null && totalCredits < 10;

  return (
    <Card
      title={<span><SoundOutlined style={{ marginRight: 8 }} />Генерація пісні (Suno)</span>}
      extra={
        <Space>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            loading={balanceFetching}
            onClick={() => refetchBalance()}
          >
            Перевірити баланс
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">

        {/* Error alert */}
        {hasError && (
          <Alert
            message="Помилка генерації пісні"
            description={order.song_error}
            type="error"
            showIcon
            action={
              <Button size="small" type="primary" danger onClick={onRetry} loading={retryLoading}>
                Спробувати знову
              </Button>
            }
          />
        )}

        {/* Processing indicator */}
        {isSunoState && !hasError && (
          <Space>
            <Spin size="small" />
            <Text type="secondary">Генерація пісні в процесі…</Text>
          </Space>
        )}

        {/* Suno status + task id */}
        {(order.song_status || order.song_suno_task_id) && (
          <Descriptions column={1} size="small" bordered>
            {order.song_status && (
              <Descriptions.Item label="Suno статус">
                <Tag color={SUNO_STATUS_COLOR[order.song_status] || 'default'}>
                  {SUNO_STATUS_LABEL[order.song_status] || order.song_status.toUpperCase()}
                </Tag>
              </Descriptions.Item>
            )}
            {order.song_suno_task_id && (
              <Descriptions.Item label="Task ID">
                <Text code style={{ fontSize: 11 }}>{order.song_suno_task_id}</Text>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}

        {/* API key balance */}
        {sunoBalance && (
          <div>
            <Text strong style={{ fontSize: 13 }}>Баланс API ключів Suno:</Text>
            <div style={{ marginTop: 6 }}>
              <Space wrap>
                {sunoBalance.keys.map((k) => (
                  <Tag
                    key={k.key_index}
                    color={
                      k.status === 'no_key' ? 'default' :
                      k.credits === null ? 'orange' :
                      k.credits < 10 ? 'red' :
                      k.credits < 50 ? 'warning' : 'green'
                    }
                  >
                    Ключ #{k.key_index}:{' '}
                    {k.status === 'no_key' ? 'не налаштовано' :
                     k.credits === null ? 'помилка запиту' :
                     `${k.credits} кредитів`}
                  </Tag>
                ))}
                <Tag color="blue">Разом: {sunoBalance.total_credits}</Tag>
              </Space>
            </div>
            {lowCredits && (
              <Alert
                style={{ marginTop: 8 }}
                message={`Низький баланс Suno: ${totalCredits} кредитів`}
                description="Поповніть рахунок на sunoapi.org або додайте додатковий API ключ."
                type="warning"
                showIcon
              />
            )}
          </div>
        )}

      </Space>
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

export const OrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const orderId = id || '';

  const { data: order, isLoading, error } = useOrder(orderId);
  const { data: history } = useOrderHistory(orderId);

  const approveMutation = useApproveOrder();
  const rejectMutation = useRejectOrder();
  const regenerateMutation = useRegenerateLyrics();
  const retryMutation = useRetryGeneration();
  const cancelMutation = useCancelOrder();
  const pauseMutation = usePauseOrder();
  const resumeMutation = useResumeOrder();
  const confirmPaymentMutation = useConfirmPayment();
  const forceTransitionMutation = useForceTransition();
  const resendSongMutation = useResendSong();

  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [targetStatus, setTargetStatus] = useState<string>('');

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <Alert
        message="Помилка завантаження"
        description={(error as Error)?.message || 'Замовлення не знайдено'}
        type="error"
        showIcon
      />
    );
  }

  const handleApprove = async () => {
    try {
      await approveMutation.mutateAsync(orderId!);
      message.success('Замовлення схвалено');
    } catch (err) {
      message.error('Помилка схвалення');
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      message.warning('Вкажіть причину відхилення');
      return;
    }
    try {
      await rejectMutation.mutateAsync({ orderId: orderId!, reason: rejectReason });
      message.success('Замовлення відхилено');
      setRejectModalVisible(false);
      setRejectReason('');
    } catch (err) {
      message.error('Помилка відхилення');
    }
  };

  const handleRegenerate = async () => {
    try {
      await regenerateMutation.mutateAsync(orderId!);
      message.success('Запущено регенерацію тексту');
    } catch (err) {
      message.error('Помилка регенерації');
    }
  };

  const handleRetry = async () => {
    try {
      await retryMutation.mutateAsync(orderId!);
      message.success('Замовлення повторюється');
    } catch (err) {
      message.error('Помилка повтору');
    }
  };

  const handleCancel = async () => {
    Modal.confirm({
      title: 'Скасувати замовлення?',
      content: 'Ця дія незворотня. Замовлення буде скасовано.',
      okText: 'Так, скасувати',
      cancelText: 'Ні',
      okType: 'danger',
      onOk: async () => {
        try {
          await cancelMutation.mutateAsync(orderId!);
          message.success('Замовлення скасовано');
        } catch (err) {
          message.error('Помилка скасування');
        }
      }
    });
  };

  const handleConfirmPayment = () => {
    Modal.confirm({
      title: 'Підтвердити оплату вручну?',
      content: (
        <div>
          <p>Ця дія підтвердить оплату замовлення <strong>{orderId}</strong> без webhook від Monobank.</p>
          <p>Використовуйте, якщо:</p>
          <ul>
            <li>webhook від Monobank не надійшов, але оплата пройшла</li>
            <li>оплата прийнята готівкою або офлайн</li>
          </ul>
          <p>Замовлення буде переведено в статус <strong>PAYMENT_CONFIRMED</strong> і почнеться генерація тексту.</p>
        </div>
      ),
      okText: 'Підтвердити оплату',
      cancelText: 'Скасувати',
      okButtonProps: { loading: confirmPaymentMutation.isPending },
      onOk: async () => {
        try {
          await confirmPaymentMutation.mutateAsync(orderId!);
          message.success('Оплату підтверджено. Розпочато генерацію тексту.');
        } catch (err: any) {
          message.error(err?.response?.data?.detail || 'Помилка підтвердження оплати');
        }
      }
    });
  };

  const handlePause = async () => {
    try {
      await pauseMutation.mutateAsync(orderId!);
      message.success('Замовлення призупинено');
    } catch (err) {
      message.error('Помилка призупинення');
    }
  };

  const handleResume = async () => {
    try {
      await resumeMutation.mutateAsync(orderId!);
      message.success('Замовлення відновлено');
    } catch (err) {
      message.error('Помилка відновлення');
    }
  };

  const handleForceTransition = () => {
    if (!targetStatus) {
      message.warning('Оберіть статус');
      return;
    }
    Modal.confirm({
      title: 'Примусовий перехід статусу',
      icon: <WarningOutlined style={{ color: '#faad14' }} />,
      content: (
        <div>
          <p>
            Перевести замовлення <strong>{orderId}</strong> у стан{' '}
            <strong>{targetStatus}</strong>?
          </p>
          <p style={{ color: '#888', fontSize: 12 }}>
            Буде запущено відповідні Celery-задачі (генерація тексту/музики,
            сповіщення клієнту тощо).
          </p>
        </div>
      ),
      okText: 'Перейти',
      cancelText: 'Скасувати',
      onOk: async () => {
        try {
          await forceTransitionMutation.mutateAsync({ orderId: orderId!, newStatus: targetStatus });
          message.success(`Статус змінено на ${targetStatus}`);
          setTargetStatus('');
        } catch (err: any) {
          message.error(err?.response?.data?.detail || 'Помилка зміни статусу');
        }
      },
    });
  };

  const handleResendSong = () => {
    Modal.confirm({
      title: 'Відправити пісню повторно?',
      content: (
        <div>
          <p>Пісня буде повторно відправлена клієнту:</p>
          <ul>
            {order.channel && <li>Через месенджер ({order.channel})</li>}
            {order.client_email && <li>На email ({order.client_email})</li>}
          </ul>
        </div>
      ),
      okText: 'Відправити',
      cancelText: 'Скасувати',
      okButtonProps: { loading: resendSongMutation.isPending },
      onOk: async () => {
        try {
          await resendSongMutation.mutateAsync(orderId!);
          message.success('Пісню відправлено клієнту');
        } catch (err: any) {
          message.error(err?.response?.data?.detail || 'Помилка відправки пісні');
        }
      }
    });
  };

  // Available actions based on FSM state
  const getActions = () => {
    const actions: JSX.Element[] = [];

    if (order.status === 'PAYMENT_PENDING') {
      actions.push(
        <Button
          key="confirm-payment"
          type="primary"
          icon={<DollarOutlined />}
          onClick={handleConfirmPayment}
          loading={confirmPaymentMutation.isPending}
          style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
        >
          Підтвердити оплату
        </Button>
      );
    }

    // "Launch" button for states where automated processing should be running
    // but might be stuck (e.g. Celery was down when payment was confirmed)
    if (['PAYMENT_CONFIRMED', 'INFO_COLLECTING', 'LYRICS_GENERATING', 'LYRICS_APPROVED', 'SUNO_GENERATING'].includes(order.status)) {
      actions.push(
        <Button
          key="launch"
          type="primary"
          icon={<CaretRightOutlined />}
          onClick={handleRetry}
          loading={retryMutation.isPending}
          style={{ backgroundColor: '#1677ff', borderColor: '#1677ff' }}
        >
          Запустити в роботу
        </Button>
      );
    }

    if (order.status === 'LYRICS_REVIEW') {
      actions.push(
        <Button
          key="approve"
          type="primary"
          icon={<CheckOutlined />}
          onClick={handleApprove}
          loading={approveMutation.isPending}
        >
          Схвалити текст
        </Button>
      );
      actions.push(
        <Button
          key="regenerate"
          icon={<ReloadOutlined />}
          onClick={handleRegenerate}
          loading={regenerateMutation.isPending}
        >
          Регенерувати текст
        </Button>
      );
    }

    if (order.status === 'QUALITY_CHECK') {
      actions.push(
        <Button
          key="approve"
          type="primary"
          icon={<CheckOutlined />}
          onClick={handleApprove}
          loading={approveMutation.isPending}
        >
          Схвалити пісню
        </Button>
      );
      actions.push(
        <Button
          key="retry"
          icon={<ReloadOutlined />}
          onClick={handleRetry}
          loading={retryMutation.isPending}
        >
          Повторити генерацію
        </Button>
      );
    }

    if (['LYRICS_GENERATING', 'SUNO_GENERATING'].includes(order.status)) {
      actions.push(
        <Button
          key="pause"
          icon={<PauseOutlined />}
          onClick={handlePause}
          loading={pauseMutation.isPending}
        >
          Призупинити
        </Button>
      );
    }

    if (order.status === 'ERROR') {
      actions.push(
        <Button
          key="retry"
          type="primary"
          icon={<ReloadOutlined />}
          onClick={handleRetry}
          loading={retryMutation.isPending}
        >
          Повторити
        </Button>
      );
      actions.push(
        <Button
          key="resume"
          icon={<PlayCircleOutlined />}
          onClick={handleResume}
          loading={resumeMutation.isPending}
        >
          Відновити
        </Button>
      );
    }

    // Common actions
    actions.push(
      <Button
        key="reject"
        danger
        icon={<CloseOutlined />}
        onClick={() => setRejectModalVisible(true)}
      >
        Відхилити
      </Button>
    );

    if (!['CANCELLED', 'COMPLETED', 'REFUNDED'].includes(order.status)) {
      actions.push(
        <Button
          key="cancel"
          danger
          icon={<StopOutlined />}
          onClick={handleCancel}
          loading={cancelMutation.isPending}
        >
          Скасувати замовлення
        </Button>
      );
    }

    return actions;
  };

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Header */}
        <Card>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={2} style={{ margin: 0 }}>
                Замовлення {order.order_id}
              </Title>
              <Text type="secondary">
                Створено: {formatDate(order.created_at)}
              </Text>
            </Col>
            <Col>
              <StatusBadge status={order.status} />
            </Col>
          </Row>
        </Card>

        {/* Actions */}
        <Card title="Дії">
          <Space wrap>{getActions()}</Space>
        </Card>

        {/* Manual Control */}
        <Card
          title={
            <span>
              <WarningOutlined style={{ color: '#faad14', marginRight: 8 }} />
              Ручне управління
            </span>
          }
          style={{ borderColor: '#faad14' }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <Text strong>Примусовий перехід стану</Text>
              <Tooltip title="Запускає FSM-колбеки для нового стану (Celery-задачі, повідомлення клієнту тощо)">
                <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                  ⓘ запускає задачі
                </Text>
              </Tooltip>
              <div style={{ marginTop: 8 }}>
                <Space>
                  <Select
                    style={{ width: 220 }}
                    placeholder="Оберіть статус..."
                    value={targetStatus || undefined}
                    onChange={setTargetStatus}
                    options={[
                      { value: 'INFO_COLLECTING',   label: '📋 INFO_COLLECTING — генерація тексту' },
                      { value: 'LYRICS_REVIEW',     label: '📝 LYRICS_REVIEW — надіслати текст клієнту' },
                      { value: 'LYRICS_APPROVED',   label: '✅ LYRICS_APPROVED — запустити Suno' },
                      { value: 'SUNO_GENERATING',   label: '🎵 SUNO_GENERATING — генерація музики' },
                      { value: 'QUALITY_CHECK',     label: '🔍 QUALITY_CHECK' },
                      { value: 'DELIVERY',          label: '📦 DELIVERY — відправити пісню' },
                      { value: 'COMPLETED',         label: '✨ COMPLETED' },
                      { value: 'MANUAL',            label: '⏸ MANUAL — призупинити' },
                      { value: 'PAYMENT_PENDING',   label: '💳 PAYMENT_PENDING' },
                      { value: 'PAYMENT_CONFIRMED', label: '✅ PAYMENT_CONFIRMED' },
                      { value: 'CANCELLED',         label: '❌ CANCELLED' },
                    ]}
                  />
                  <Button
                    type="primary"
                    icon={<ThunderboltOutlined />}
                    onClick={handleForceTransition}
                    loading={forceTransitionMutation.isPending}
                    disabled={!targetStatus}
                    style={{ backgroundColor: '#fa8c16', borderColor: '#fa8c16' }}
                  >
                    Перейти
                  </Button>
                </Space>
              </div>
            </div>
            <Divider style={{ margin: '8px 0' }} />
            <div>
              <Text strong>Розумний перезапуск</Text>
              <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                — автоматично визначає де відновити
              </Text>
              <div style={{ marginTop: 8 }}>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleRetry}
                  loading={retryMutation.isPending}
                >
                  Retry (авто)
                </Button>
              </div>
            </div>
          </Space>
        </Card>

        {/* Order Info */}
        <Card title="Інформація про замовлення">
          <Descriptions bordered column={2}>
            <Descriptions.Item label="ID">{order.order_id}</Descriptions.Item>
            <Descriptions.Item label="Статус">
              <StatusBadge status={order.status} />
            </Descriptions.Item>
            <Descriptions.Item label="Клієнт">{order.client_name || '—'}</Descriptions.Item>
            <Descriptions.Item label="Телефон">
              {formatPhone(order.client_phone)}
            </Descriptions.Item>
            <Descriptions.Item label="Канал">
              {order.channel
                ? <Tag>{CHANNEL_LABELS[order.channel as keyof typeof CHANNEL_LABELS] || order.channel}</Tag>
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Сума">
              <strong>{formatCurrency(order.amount)}</strong>
            </Descriptions.Item>
            <Descriptions.Item label="Привід">{order.occasion || '—'}</Descriptions.Item>
            <Descriptions.Item label="Настрій">{order.mood || '—'}</Descriptions.Item>
            <Descriptions.Item label="Стиль">{order.style || '—'}</Descriptions.Item>
            <Descriptions.Item label="Імена">
              {order.names?.join(', ') || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Статус оплати">{order.payment_status || '—'}</Descriptions.Item>
            <Descriptions.Item label="Текст схвалено">
              {order.lyrics_approved ? 'Так' : 'Ні'}
            </Descriptions.Item>
            <Descriptions.Item label="Оплачено">
              {order.paid_at ? formatDate(order.paid_at) : 'Не оплачено'}
            </Descriptions.Item>
          </Descriptions>

          {order.additional_info && (
            <>
              <Divider />
              <Title level={5}>Додаткова інформація</Title>
              <Paragraph>{order.additional_info}</Paragraph>
            </>
          )}
        </Card>

        {/* Lyrics */}
        {order.lyrics_text && (
          <Card title="Текст пісні">
            <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{order.lyrics_text}</Paragraph>
          </Card>
        )}

        {/* Song Generation Status */}
        <SongGenerationBlock order={order} onRetry={handleRetry} retryLoading={retryMutation.isPending} />

        {/* Song */}
        {(order.song_download_url || order.song_url) && (
          <Card
            title={
              <span>
                <SoundOutlined style={{ marginRight: 8 }} />
                Пісня
              </span>
            }
            extra={
              order.song_file_exists && (
                <Space>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={handleResendSong}
                    loading={resendSongMutation.isPending}
                  >
                    Відправити повторно
                  </Button>
                  <a
                    href={order.song_download_url || order.song_url || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button icon={<DownloadOutlined />}>Завантажити</Button>
                  </a>
                </Space>
              )
            }
          >
            <audio controls style={{ width: '100%' }}>
              <source src={order.song_download_url || order.song_url || ''} type="audio/mpeg" />
            </audio>
            {order.song_duration && (
              <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                Тривалість: {Math.floor(order.song_duration / 60)}:{String(order.song_duration % 60).padStart(2, '0')}
              </Text>
            )}
            {!order.song_file_exists && order.song_url && (
              <Alert
                style={{ marginTop: 12 }}
                message="Файл пісні не знайдено на сервері"
                description="Файл міг бути видалений. Спробуйте перегенерувати пісню."
                type="warning"
                showIcon
              />
            )}
          </Card>
        )}

        {/* FSM History */}
        {history && history.length > 0 && (
          <Card title="Історія змін статусу">
            <FSMTimeline transitions={history} />
          </Card>
        )}
      </Space>

      {/* Reject Modal */}
      <Modal
        title="Відхилити замовлення"
        open={rejectModalVisible}
        onOk={handleReject}
        onCancel={() => setRejectModalVisible(false)}
        okText="Відхилити"
        cancelText="Скасувати"
        okButtonProps={{ danger: true, loading: rejectMutation.isPending }}
      >
        <TextArea
          rows={4}
          placeholder="Вкажіть причину відхилення..."
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
        />
      </Modal>
    </div>
  );
};
